import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { CourseSearchRecord, DepartmentRecord } from "../../shared/types";
import { StaticDataState } from "../components/StaticDataState";
import { formatCourseOfferingPreview } from "../lib/offerings";
import { fetchCourseSearchIndex, fetchDepartments } from "../lib/static-data";

export function DepartmentPage() {
  const { code = "" } = useParams();
  const normalizedCode = code.toUpperCase();
  const [department, setDepartment] = useState<DepartmentRecord | null>(null);
  const [courses, setCourses] = useState<CourseSearchRecord[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setHasLoaded(false);
    setError(null);
    void Promise.all([fetchDepartments(), fetchCourseSearchIndex()])
      .then(([departments, courseIndex]) => {
        setDepartment(departments.find((candidate) => candidate.code === normalizedCode) ?? null);
        setCourses(
          courseIndex
            .filter((course) => course.department === normalizedCode)
            .sort((left, right) => {
              if (right.legacyReviewCount !== left.legacyReviewCount) {
                return right.legacyReviewCount - left.legacyReviewCount;
              }
              return left.shortName.localeCompare(right.shortName);
            }),
        );
        setHasLoaded(true);
      })
      .catch(() => {
        setDepartment(null);
        setCourses([]);
        setError("Could not load this department right now.");
        setHasLoaded(true);
      });
  }, [normalizedCode, reloadKey]);

  if (!hasLoaded) {
    return <StaticDataState title="Loading department..." />;
  }

  if (error) {
    return <StaticDataState title="Department could not load." message={error} error onRetry={() => setReloadKey((value) => value + 1)} />;
  }

  if (!department) {
    return (
      <section className="panel stack-md">
        <div className="section-heading">
          <p className="eyebrow">Department</p>
          <h1>{normalizedCode} is not in the catalog.</h1>
        </div>
          <Link to="/departments/" className="primary-link">
          Back to departments
        </Link>
      </section>
    );
  }

  const reviewedCourses = courses.filter((course) => course.legacyReviewCount > 0);

  return (
    <div className="stack-xl">
      <section className="panel stack-lg">
        <div className="section-heading">
          <p className="eyebrow">{department.code}</p>
          <h1>{department.name || department.code}</h1>
        </div>
        <p className="lede">
          Browse {department.code} course history, read student reviews, and check the latest Dartmouth
          offerings and instructors before your next quarter.
        </p>
        <div className="hero-actions">
          <Link to={`/search/?department=${department.code}`} className="primary-link">
            Search within {department.code}
          </Link>
          <Link to="/departments/" className="secondary-link">
            All departments
          </Link>
        </div>
        <div className="feature-grid">
          <article className="department-summary">
            <span>Courses</span>
            <strong>{courses.length}</strong>
          </article>
          <article className="department-summary">
            <span>Reviewed</span>
            <strong>{reviewedCourses.length}</strong>
            <small>Courses with student reviews</small>
          </article>
        </div>
      </section>

      <section className="panel stack-lg">
        <div className="section-heading">
          <p className="eyebrow">Courses</p>
          <h2>{department.code} catalog</h2>
        </div>
        <div className="results-grid">
          {courses.map((course) => {
            const offeringPreview = formatCourseOfferingPreview(course);

            return (
              <Link key={course.slug} className="result-card" to={`/course/${course.slug}/`}>
                <strong>{course.shortName}</strong>
                <span>{course.title}</span>
                <small>{course.legacyReviewCount} reviews</small>
                {offeringPreview ? <small>{offeringPreview}</small> : null}
                {course.professorNames.length > 0 ? (
                  <small>Professors: {course.professorNames.slice(0, 3).join(", ")}</small>
                ) : null}
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
